import { AnimatePresence, motion } from "motion/react"
import { useState } from "react"

export function SupportButton() {
  const [isHovered, setIsHovered] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const SUPPORT_EMAIL = import.meta.env.VITE_APP_SUPPORT_EMAIL
  const SUPPORT_PHONE = import.meta.env.VITE_APP_SUPPORT_PHONE
  const SUPPORT_EMAIL_SUBJECT = import.meta.env.VITE_APP_SUPPORT_EMAIL_SUBJECT

  return (
    // The widget parks at the viewport's bottom-right corner, where page
    // content — a card's Save button, a table's last action — also lands. Its
    // box is 56px wide while only a ~16px sliver of the button is on screen, so
    // left solid it swallows clicks on controls that look completely
    // unobstructed. Only the parts that are actually visible take the pointer.
    <motion.div
      data-testid="support-widget"
      className="pointer-events-none fixed bottom-6 right-0 z-50 flex flex-col items-end gap-3"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false)
        setIsOpen(false)
      }}
    >
      {/* Options */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.a
              href={`https://wa.me/${SUPPORT_PHONE}?text=${encodeURIComponent("Hi! I'm experiencing some problems.")}`}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2, delay: 0.05 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="pointer-events-auto mr-4 flex items-center gap-2 rounded-full bg-green-500 px-4 py-2 text-white shadow-lg hover:bg-green-600"
            >
              <WhatsAppIcon />
              <span className="text-sm font-medium">WhatsApp</span>
            </motion.a>

            <motion.a
              href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(SUPPORT_EMAIL_SUBJECT)}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="pointer-events-auto mr-4 flex items-center gap-2 rounded-full bg-blue-500 px-4 py-2 text-white shadow-lg hover:bg-blue-600"
            >
              <EmailIcon />
              <span className="text-sm font-medium">Email</span>
            </motion.a>
          </>
        )}
      </AnimatePresence>

      {/* Main Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        animate={{
          x: isHovered ? 0 : 40
        }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="pointer-events-auto flex h-12 w-14 items-center cursor-pointer justify-center rounded-l-full bg-primary text-white shadow-lg"
      >
        <motion.div
          animate={{ x: isHovered ? 0 : 15 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <SupportIcon />
        </motion.div>
      </motion.button>
    </motion.div>
  )
}

function SupportIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="size-7"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
      />
    </svg>
  )
}

function WhatsAppIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-4 w-4"
      fill="currentColor"
      viewBox="0 0 24 24"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  )
}

function EmailIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  )
}
